import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { QRCodeComponent } from 'angularx-qrcode';
import { Auth, signInWithCustomToken } from '@angular/fire/auth';
import { addDoc, collection, deleteDoc, doc, docData, Firestore, Timestamp } from '@angular/fire/firestore';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { IDesktopLoginSession } from '@soposopo/sopo-models';

const SESSION_TTL_MS = 5 * 60 * 1000;

@Component({
    selector: 'app-qr-login',
    standalone: true,
    imports: [CommonModule, IonicModule, QRCodeComponent],
    templateUrl: './qr-login.component.html',
    styleUrls: ['./qr-login.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QrLoginComponent implements OnInit, OnDestroy {
    qrData: string;
    expired = false;
    loading = true;
    error = '';

    private destroy$ = new Subject<void>();
    private docRefPath: string;
    private expiryTimeout: ReturnType<typeof setTimeout>;
    private retryTimeout: ReturnType<typeof setTimeout>;

    constructor(
        private firestore: Firestore,
        private auth: Auth,
        private ref: ChangeDetectorRef,
    ) {}

    async ngOnInit() {
        await this.createSession();
    }

    async createSession() {
        this.expired = false;
        this.error = '';
        this.loading = true;
        this.ref.detectChanges();

        const collectionRef = collection(this.firestore, 'desktopLoginSessions');
        // expireAt: Firestore TTL policy auto-deletes this doc after 5 minutes
        const docRef = await addDoc(collectionRef, {
            status: 'pending',
            createdAt: Date.now(),
            expireAt: Timestamp.fromMillis(Date.now() + SESSION_TTL_MS),
        });

        this.qrData = `https://link.playsopo.com/desktop-login?sessionId=${docRef.id}`;
        this.docRefPath = docRef.path;
        this.loading = false;
        this.ref.detectChanges();

        this.expiryTimeout = setTimeout(() => {
            this.expired = true;
            this.cleanup();
            this.ref.detectChanges();
        }, SESSION_TTL_MS);

        docData(doc(this.firestore, this.docRefPath)).pipe(
            filter((data: IDesktopLoginSession) => data?.status === 'approved' && !!data?.customToken),
            takeUntil(this.destroy$),
        ).subscribe(async (data: IDesktopLoginSession) => {
            try {
                await signInWithCustomToken(this.auth, data.customToken);
                this.cleanup();
            } catch (error) {
                this.destroy$.next();
                console.error('QR login failed:', error);
                this.error = 'Login failed. Generating new QR code...';
                this.ref.detectChanges();
                this.cleanup();
                this.retryTimeout = setTimeout(() => this.createSession(), 1500);
            }
        });
    }

    async refresh() {
        this.destroy$.next();
        this.cleanup();
        await this.createSession();
    }

    private cleanup() {
        clearTimeout(this.expiryTimeout);
        clearTimeout(this.retryTimeout);
        if (this.docRefPath) {
            deleteDoc(doc(this.firestore, this.docRefPath)).catch(() => {});
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.cleanup();
    }
}
